import { __awaiter } from 'tslib';
import { assertEx } from '@xyo-network/sdk-xyo-js';
import { Mutex } from 'async-mutex';
import md5 from 'md5';
import axios from 'axios';
import randomBytes from 'randombytes';
import isEqual from 'lodash/isEqual';

class PixelApi {
    trackEvents(events) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield axios.post('https://api.coinapp.co/t/event', events)).data;
        });
    }
}

class Referrer {
    constructor() {
        var _a, _b;
        this.session = (_a = this.getFromSession()) !== null && _a !== void 0 ? _a : document.referrer;
        sessionStorage.setItem(Referrer.storageId, this.session);
        this.local = (_b = this.getFromLocal()) !== null && _b !== void 0 ? _b : document.referrer;
        localStorage.setItem(Referrer.storageId, this.local);
    }
    getFromLocal() {
        const value = localStorage.getItem(Referrer.storageId);
        if (value && value.length > 0) {
            return value;
        }
    }
    getFromSession() {
        const value = sessionStorage.getItem(Referrer.storageId);
        if (value && value.length > 0) {
            return value;
        }
    }
    toJson() {
        if ((this.local && this.local.length > 0) || (this.session && this.session.length > 0)) {
            return {
                local: this.local,
                session: this.session,
            };
        }
    }
}
Referrer.storageId = '_coin_referrer';

class UniqueUserId {
    constructor() {
        var _a;
        this.id = (_a = localStorage.getItem(UniqueUserId.localStorageId)) !== null && _a !== void 0 ? _a : this.generateId();
        localStorage.setItem(UniqueUserId.localStorageId, this.id);
    }
    generateId() {
        return randomBytes(16).toString('base64');
    }
    toString() {
        return this.id;
    }
}
UniqueUserId.localStorageId = '_coin_cid';

class UtmFields {
    constructor() {
        var _a;
        this.fields = [];
        this.getUtmRecord = () => {
            var _a, _b;
            const record = {};
            const parsedQueryString = (_b = (_a = document.location.search.split('?')[1]) === null || _a === void 0 ? void 0 : _a.split('&')) !== null && _b !== void 0 ? _b : [];
            parsedQueryString.map((item) => {
                const [fullKey, value] = item === null || item === void 0 ? void 0 : item.split('=');
                const [keyCategory, keyName] = fullKey.split('_');
                if (keyCategory === 'utm') {
                    record[keyName] = value;
                }
            });
            return Object.keys(record).length > 0 ? record : null;
        };
        const storedString = (_a = localStorage.getItem(UtmFields.localStorageId)) !== null && _a !== void 0 ? _a : '[]';
        try {
            this.fields = JSON.parse(storedString);
        }
        catch (_b) {
            this.fields = [];
        }
        //this clears the old object version if needed
        if (!Array.isArray(this.fields)) {
            this.fields = [];
        }
        this.update();
        localStorage.setItem(UtmFields.localStorageId, JSON.stringify(this.fields));
    }
    //check the query string and if there an new/updated utm values, add them to the fields
    update() {
        var _a;
        const record = this.getUtmRecord();
        if (record) {
            if (!isEqual(this.fields[this.fields.length - 1], record)) {
                this.fields.push(record);
            }
        }
        return (_a = this.fields) !== null && _a !== void 0 ? _a : undefined;
    }
    toString() {
        return JSON.stringify(this.fields);
    }
}
UtmFields.localStorageId = '_coin_utm';

class XyPixel {
    constructor(pixelId) {
        this.queue = [];
        this.cid = new UniqueUserId().id;
        this.queueMutex = new Mutex();
        this.pixelId = pixelId;
    }
    identify(email) {
        this.email = email;
        this.email_hash = email ? md5(email) : undefined;
    }
    tryFlushQueue() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.queueMutex.runExclusive(() => __awaiter(this, void 0, void 0, function* () {
                if (this.queue.length === 0)
                    return;
                const api = XyPixel.api;
                if (api) {
                    const events = this.queue;
                    this.queue = [];
                    try {
                        yield api.trackEvents(events);
                    }
                    catch (ex) {
                        if (events) {
                            //put it back since it failed
                            this.queue = this.queue.concat(events);
                        }
                        console.error(ex);
                    }
                }
            }));
        });
    }
    send(event, fields) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const utm = XyPixel.utmFields.update();
            const referrer = new Referrer();
            this.queue.push(Object.assign({
                cid: this.cid,
                create_time: Date.now(),
                email_hash: (_a = this.email_hash) !== null && _a !== void 0 ? _a : undefined,
                event,
                fields,
                host: document.location.host,
                pixel: this.pixelId,
                referrer: referrer.toJson(),
                utm,
            }));
            yield this.tryFlushQueue();
        });
    }
    static get instance() {
        return assertEx(this._instance, 'XyPixel uninitialized');
    }
    static init(pixelId) {
        this._instance = new XyPixel(pixelId);
    }
    static selectApi(api) {
        this.api = api;
    }
}
XyPixel.utmFields = new UtmFields();
XyPixel.api = new PixelApi();

class UserEventHandler {
}

class XyUserEventHandler extends UserEventHandler {
    constructor() {
        super();
    }
    testStarted(fields) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield XyPixel.instance.send('TestStarted', fields);
        });
    }
    funnelStarted(fields) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield XyPixel.instance.send('FunnelStarted', fields);
        });
    }
    viewContent(fields) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield XyPixel.instance.send('ViewContent', fields);
        });
    }
    userClick(fields) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield XyPixel.instance.send('ViewPage', fields);
        });
    }
}

export { PixelApi, Referrer, UniqueUserId, UserEventHandler, UtmFields, XyPixel, XyUserEventHandler };
